// src/components/LabelPrintModal.tsx
// Печать этикетки товара: штрих-код Code128 из SKU + SKU + бренд + номер детали.
// Размер этикетки 40×30 мм (по умолчанию), меняется в диалоге печати браузера.
import React, { useMemo, useState } from 'react';
import { Modal, InputNumber, Button, Tag, Space } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import JsBarcode from 'jsbarcode';

export type LabelProduct = {
  sku: string;
  brand: string;
  partNumber: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  product: LabelProduct | null;
};

// Рендер Code128 в PNG dataURL. Бросает, если value пустой/некорректный.
function makeBarcodeDataUrl(value: string): string {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: 2,
    height: 50,
    displayValue: false,
    margin: 0,
  });
  return canvas.toDataURL('image/png');
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const LabelPrintModal: React.FC<Props> = ({ open, onClose, product }) => {
  const [qty, setQty] = useState(1);

  const sku = product?.sku?.trim() ?? '';

  const previewUrl = useMemo(() => {
    if (!open || !sku) return '';
    try {
      return makeBarcodeDataUrl(sku);
    } catch {
      return '';
    }
  }, [open, sku]);

  const handlePrint = () => {
    if (!product || !sku || !previewUrl) return;
    const copies = Math.max(1, qty || 1);

    const labelHtml = `
      <div class="label">
        <img src="${previewUrl}" />
        <div class="sku">${escapeHtml(sku)}</div>
        <div class="meta">${escapeHtml(product.brand)} ${escapeHtml(product.partNumber)}</div>
      </div>`;
    const all = Array.from({ length: copies }, () => labelHtml).join('');

    const win = window.open('', '_blank', 'width=420,height=640');
    if (!win) return;
    win.document.write(`<!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Этикетки</title>
          <style>
            @page { size: 40mm 30mm; margin: 0; }
            * { box-sizing: border-box; }
            body { margin: 0; font-family: Arial, sans-serif; }
            .label {
              width: 40mm; height: 30mm; padding: 1.5mm;
              display: flex; flex-direction: column;
              align-items: center; justify-content: center;
              text-align: center; overflow: hidden;
              page-break-after: always;
            }
            .label img { width: 37mm; height: 13mm; object-fit: contain; }
            .sku { font-size: 10pt; font-weight: bold; letter-spacing: 1px; margin-top: 0.5mm; }
            .meta { font-size: 7pt; line-height: 1.1; margin-top: 0.5mm; }
          </style>
          <script>
            window.onafterprint = function () { window.close(); };
          </script>
        </head>
        <body onload="window.print()">${all}</body>
      </html>`);
    win.document.close();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Печать этикетки"
      footer={[
        <Button key="close" onClick={onClose}>Закрыть</Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          disabled={!sku || !previewUrl}
        >
          Печать
        </Button>,
      ]}
      destroyOnHidden
    >
      {!sku ? (
        <Tag color="error">У товара нет SKU — сначала присвойте SKU в карточке товара</Tag>
      ) : !previewUrl ? (
        <Tag color="error">Не удалось сформировать штрих-код для «{sku}»</Tag>
      ) : (
        <div>
          <div
            style={{
              border: '1px dashed #555',
              borderRadius: 6,
              padding: 12,
              width: 'fit-content',
              margin: '0 auto 16px',
              background: '#fff',
              textAlign: 'center',
            }}
          >
            <img src={previewUrl} alt="barcode" style={{ width: 180, height: 56, objectFit: 'contain', display: 'block' }} />
            <div style={{ color: '#000', fontWeight: 700, letterSpacing: 1, marginTop: 4 }}>{sku}</div>
            <div style={{ color: '#000', fontSize: 12 }}>
              {product?.brand} {product?.partNumber}
            </div>
          </div>

          <Space align="center">
            <span>Количество копий:</span>
            <InputNumber min={1} max={200} value={qty} onChange={(v) => setQty(Number(v) || 1)} style={{ width: 90 }} />
          </Space>

          <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 12 }}>
            Размер этикетки 40×30 мм. Размер можно изменить в диалоге печати браузера.
          </div>
        </div>
      )}
    </Modal>
  );
};

export default LabelPrintModal;
